import React from 'react';
import { getLanguage } from '@deadpan/shared';
import { LanguageSwitcher } from './LanguageSwitcher';
import { CopyrightWatermark } from './CopyrightWatermark';
import { MaintenanceBanner } from './MaintenanceBanner';
import { useTranslations } from '../hooks/useTranslations';
import { useSocket, clearPlayerSeat } from '../contexts/SocketContext';

interface PageWrapperProps {
  children: React.ReactNode;
  sidePanel?: React.ReactNode;
  gradient?: 'default' | 'lighter';
  /** Rendered under the language switcher (e.g. host lobby actions). */
  headerAccessory?: React.ReactNode;
  showToolsButton?: boolean;
  onToolsClick?: () => void;
  /** Homepage-only shortcut next to Newt's case. */
  showWeekendHahaButton?: boolean;
  onWeekendHahaClick?: () => void;
  showLeaveButton?: boolean;
  showEndGameButton?: boolean;
  toolsOpen?: boolean;
  weekendHahaOpen?: boolean;
}

export const PageWrapper: React.FC<PageWrapperProps> = ({ 
  children, 
  sidePanel,
  gradient = 'default',
  headerAccessory,
  showToolsButton = false,
  onToolsClick,
  showWeekendHahaButton = false,
  onWeekendHahaClick,
  showLeaveButton = false,
  showEndGameButton = false,
  toolsOpen = false,
  weekendHahaOpen = false,
}) => {
  const t = useTranslations();
  const { socket, isMaintenance, currentRoom, myPlayerId } = useSocket();
  const overlayOpen = toolsOpen || weekendHahaOpen;
  const showTopLeftButtons =
    !overlayOpen &&
    ((showToolsButton && !!onToolsClick) || (showWeekendHahaButton && !!onWeekendHahaClick));

  const bgClass = gradient === 'lighter' 
    ? 'bg-gradient-to-br from-indigo-100 via-purple-100 to-pink-100'
    : 'bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50';
  
  const handleLeaveRoom = () => {
    if (socket) {
      socket.emit('client:leave-room');
      clearPlayerSeat();
    }
  };

  const handleReturnToLobby = () => {
    if (!socket) return;
    socket.emit('client:end-game');
  };

  const handleRestartGame = () => {
    if (!socket) return;
    if (currentRoom?.gameType === 'pensieve') {
      // For Pensieve: reshuffle order and immediately restart with the new order.
      socket.emit('client:start-new-game');
      socket.emit('client:pensieve-new-order');
      socket.emit('client:start-game');
      return;
    }
    // Emulate host flow: reset to waiting first, then immediately start again.
    socket.emit('client:start-new-game');
    socket.emit('client:start-game');
  };
  
  return (
    <div className={`min-h-screen ${bgClass} flex items-center justify-center p-3 sm:p-4 pb-20 sm:pb-16 relative overflow-x-hidden`}>
      <MaintenanceBanner isMaintenance={isMaintenance} />
      {showTopLeftButtons && (
        <div
          className={`absolute left-2 sm:left-4 top-2 sm:top-4 z-[60] flex flex-col items-start gap-2 sm:gap-3 ${
            isMaintenance ? 'top-14 sm:top-16' : ''
          }`}
        >
          {showToolsButton && onToolsClick && (
            <button
              onClick={onToolsClick}
              className="px-3 py-1.5 sm:px-4 sm:py-2 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-lg font-semibold hover:from-indigo-600 hover:to-purple-600 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 text-xs sm:text-sm"
            >
              {t.ui.toolsButton ?? (getLanguage() === 'zh' ? '工具' : 'Tools')}
            </button>
          )}
          {showWeekendHahaButton && onWeekendHahaClick && (
            <button
              onClick={onWeekendHahaClick}
              className="px-3 py-1.5 sm:px-4 sm:py-2 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-lg font-semibold hover:from-indigo-600 hover:to-purple-600 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 text-xs sm:text-sm"
            >
              {t.ui.weekendHahaTab ?? (getLanguage() === 'zh' ? '周末有嘻嘻哈哈' : 'Weekend hangout')}
            </button>
          )}
        </div>
      )}
      <div className={`absolute right-2 sm:right-4 top-2 sm:top-4 flex flex-col items-end gap-2 sm:gap-3 z-[60] ${isMaintenance ? 'top-14 sm:top-16' : ''}`}>
        <LanguageSwitcher />
        {!overlayOpen && headerAccessory}
      </div>
      {showLeaveButton && (
        <div className="absolute bottom-3 sm:bottom-4 right-2 sm:right-4 z-20 flex flex-row gap-2 items-center">
          {showEndGameButton && currentRoom?.hostId === myPlayerId && (
            <>
              <button
                onClick={handleReturnToLobby}
                className="px-3 py-1.5 sm:px-4 sm:py-2 bg-gradient-to-r from-sky-400 to-cyan-500 text-white rounded-lg font-semibold hover:from-sky-500 hover:to-cyan-600 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 text-xs sm:text-sm"
              >
                {t.ui.endGame}
              </button>
              <button
                onClick={handleRestartGame}
                className="px-3 py-1.5 sm:px-4 sm:py-2 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg font-semibold hover:from-orange-600 hover:to-amber-600 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 text-xs sm:text-sm"
              >
                {currentRoom?.gameType === 'pensieve' ? t.games.pensieve.newOrderButton : t.ui.restartGame}
              </button>
            </>
          )}
          <button
            onClick={handleLeaveRoom}
            className="px-3 py-1.5 sm:px-4 sm:py-2 bg-gradient-to-r from-red-500 to-rose-500 text-white rounded-lg font-semibold hover:from-red-600 hover:to-rose-600 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 text-xs sm:text-sm"
          >
            {t.ui.leaveRoom}
          </button>
        </div>
      )}
      <div className={`w-full flex flex-col items-center ${sidePanel ? 'lg:flex-row lg:items-start lg:justify-center lg:gap-6 xl:gap-8 lg:pr-[320px]' : ''}`}>
        <div className="flex justify-center w-full">
          {children}
        </div>
        {sidePanel && (
          <div className="w-full mt-4 sm:mt-5 lg:mt-16 lg:w-auto lg:absolute lg:right-4 xl:right-6 lg:top-24 z-20 flex justify-center">
            {sidePanel}
          </div>
        )}
      </div>
      <CopyrightWatermark />
    </div>
  );
};

